import { ViewChild, ElementRef, HostListener, OnInit, AfterViewInit, ChangeDetectorRef, Input, Directive } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { Subject } from 'rxjs';
import { AppTableDirective } from '../../directive/app-table.directive';
import { AppTableOptions } from '../../model/table/appTableOptions.model';
import { AuthService } from '@app/core/auth/auth.service';
import { FormGroup, ValidationErrors } from '@angular/forms';
import { SpinnerService } from '@app/core/spinner/spinner.service';
import { InfoService } from '@app/core/info/info.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AbstractEntityComponent } from '../AbstractEntityComponent';
import { EntityInterface } from '@app/shared/model/entity/interfaces/entityInterface.model';
import { environment } from '@env/environment';
import { ConfirmationService } from '@app/core/confirmation/confirmation.service';
import { DateUtil } from '@app/shared/util/DateUtil.model';
import { EntityServiceInterface } from '@app/shared/service/interfaces/EntityServiceInterface';

@Directive()
export abstract class AbstractTableComponent extends AbstractEntityComponent implements OnInit, AfterViewInit {

  // ****************************** Propiedades **********************

  @Input() withHeader = true;              // define si debe tener cabecera o no
  @Input() withBorder = true;              // define si debe tener borde o no
  @Input() withShadow = true;              // define si debe tener sombra o no
  @Input() recordsNumber = 26;             // define el número de registros

  // directiva de tabla de cronoss
  @ViewChild(DataTableDirective)
  dataTable: DataTableDirective;

  // para la tabla gestionada por angular
  // directiva de tabla customizada
  @ViewChild(AppTableDirective)
  appTable: AppTableDirective;

  // capturar el input para la busqueda
  @ViewChild('searchTableInput') searchTableInput: ElementRef;

  // primer elemento a coger el focus en el filtro
  @ViewChild('firstFocusFilter') firstFocusfilter: ElementRef;

  // parametros de la tabla
  // lo lógico sería lo siguiente
  // dtOptions: DataTables.Settings = {};
  // pero entonces no te permite utilizar extensiones
  // dtOptions: AppTableOptions;
  // trigger para pintar la tabla de nuevo
  appTableTrigger: Subject<any> = new Subject();

  detailForm: FormGroup;
  // para la tabla gestionada por json
  // parametros de la tabla
  appTableOptions: AppTableOptions;

  // id de la tabla para pasarlo a la directiva de busqueda
  // tableId: string;
  // directive de input de busqueda
  // @ViewChild(InputTableSearchDirective, {static: false}) inputTableSearch: InputTableSearchDirective;

  isShowFilter: boolean;
  isFilter = false;

  // listado de elementos
  entities: Array<EntityInterface>;
  // formulario reactivo para filtros
  filterForm: FormGroup;
  // define si el formulario de busqueda se ha submitido o no
  filterSubmitted = false;

  // ****************************** CONSTRUCTOR ***************************

  constructor(
    public authService: AuthService,
    public spinner: SpinnerService,
    public info: InfoService,
    public modalService: NgbModal,
    public isAngularTable: boolean,
    public isDataTableFilter: boolean,
    public translateService: TranslateService,
    public changeDetection?: ChangeDetectorRef,
    public isAngularNpmDataTable = true,
    public confirmationService?: ConfirmationService,
    public deleteWithComment = false
  ) {

    super(authService, spinner, info, modalService, translateService, confirmationService, deleteWithComment);

    this.isShowFilter = false;

  }

  // ****************************** Eventos ******************************
  @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent): void {
    if (this.searchTableInput !== undefined && this.searchTableInput !== null) {
      if ((event.key === 'f' || event.key === 'F') && event.ctrlKey) {
        event.preventDefault();
        if (event.altKey) {
          this.toggleFilter();
        } else {
          this.searchTableInput.nativeElement.focus();
        }
      }
    }

    if (environment.visualFacility) {
      if ((event.key === 'e' || event.key === 'E') && event.ctrlKey) {
        event.preventDefault();
        let errors = '';
        Object.keys(this.filterForm.controls).forEach(key => {
          const controlErrors: ValidationErrors = this.filterForm.get(key).errors;
          if (controlErrors != null) {
            Object.keys(controlErrors).forEach(keyError => {
                errors = errors + `Error --> Key control: [${key}], keyError: (${keyError}) \n`;
            });
          }
        });
        if (errors !== '') {
          alert(errors);
        }
      }
    }

  }

  // ****************************** Ciclo de vida *********************

  ngOnInit(): void {
    this.ngOnInitAppAbstractTableComponent();
    // llamamos al metodo onInit del hijo
    this.onInit();
  }

  ngAfterViewInit(): void {
    this.ngAfterViewInitAppAbstractTableComponent();
    // llamamos al metodo onInit del hijo
    this.afterViewInit();
  }

  ngOnInitAppAbstractTableComponent(): void {

    // inicializamos el formulario reactivo de filtros
    this.filterForm = this.getFilterForm();

    // inicializamos las opciones de la tabla de json
    this.appTableOptions = this.getAppTableOptions();

    if (this.appTableOptions === undefined && this.appTableOptions === null) {
      throw new Error('appTableOptions is necesary');
    }

  }

  ngAfterViewInitAppAbstractTableComponent(): void {
    if (this.searchTableInput !== undefined && this.searchTableInput !== null) {
      this.searchTableInput.nativeElement.focus();
    }
    if (this.isAngularTable) {
      if (this.isAngularNpmDataTable) {
        this.appTableTrigger.next(undefined);
      } else {
        this.tableInit();
      }
    } else {
      this.tableInit();
    }
  }

  // ****************************** Metodos de acciones estandard *****

  filter(): void {

    this.filterSubmitted = true;
    if (this.filterForm.invalid) {
      return;
    } else {
      let isFilter = false;
      for (const field of Object.keys(this.filterForm.controls)) {
        const control = this.filterForm.get(field);
        if (control.value !== '') {
          isFilter = true;
        }
      }
      this.isFilter = isFilter;
      this.isShowFilter = false;
      this.doFilter();
      if (this.isDataTableFilter) {
        this.appTable.draw();
        // this.appTable.resizeColumns(1000);
      }
    }
    this.angularChangeDetection();
  }

  angularChangeDetection(): void {
    if (this.changeDetection) {
      this.changeDetection.detectChanges();
    }
  }

  removeFilter(): void {

    if (this.isFilter) {

      this.filterSubmitted = false;
      this.doRemoveFilter();
      if (this.isDataTableFilter) {
        this.appTable.draw();
      }
      this.isFilter = false;
    } else {
      this.toggleFilter();
    }
    this.angularChangeDetection();

  }

  // ****************************** Metodos ***************************

  getDateColumnRender(): any {
    return {
      display: (data: Date): string => {
        if (data) {
          return `<span>${DateUtil.getDateFormatFromDate(data)}</span>`;
        } else {
          return '<span></span>';
        }
      }
    };
  }

  getActiveColumnRender(): any {
    return {
      display: (data: boolean): string => {
        if (data) {
          return `<span class="active-text">${this.translateService.instant('common.active.male') as string}</span>`;
        } else {
          return `<span class="inactive-text">${this.translateService.instant('common.inactive.male') as string}</span>`;
        }
      }
    };
  }

  tableInit(): void {
    if (this.isAngularTable) {
      this.angularTableInit();
    } else {
      this.jqueryTableInit();
    }
  }

  tableReInit(): void {
    setTimeout(() => {
      this.appTable.reInit();
    }, 200);
  }

  tableDestroy(): void {
    this.appTable.destroy();
  }

  // ------------------------------------------------------------------

  tableChange(entities?: Array<EntityInterface>): void {
    console.log('Table change');
    if (this.isAngularTable) {
      if (this.isAngularNpmDataTable) {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        if (this.dataTable.dtInstance) {
          void this.dataTable.dtInstance.then((dtInstance: DataTables.Api) => {
            // Destroy the table first
            dtInstance.destroy();
            // Call the dtTrigger to rerender again
            this.appTableTrigger.next(undefined);
          });
        } else {
          this.appTableTrigger.next(undefined);
        }
      } else {
        this.angularTableChange();
      }
    } else {
      this.jqueryTableChange(entities);
    }
  }

  angularTableDestroy(): void {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (this.dataTable.dtInstance) {
      void this.dataTable.dtInstance.then((dataTableCountries: DataTables.Api) => {
        dataTableCountries.destroy();
      });
    }
  }

  angularTableReload(): void {
    this.angularTableDestroy();
    this.angularTableDestroy();
  }

  toggleFilter(): void {
    this.isShowFilter = !this.isShowFilter;
    if (this.isShowFilter) {
      if (this.firstFocusfilter !== undefined && this.firstFocusfilter !== null) {
        setTimeout(() => { this.firstFocusfilter.nativeElement.focus(); }, 100);
      }
    }
    if (!this.isAngularNpmDataTable) {
      this.appTable.resizeColumns(1000);
    }
    this.angularChangeDetection();
  }

  setTabFocus(): void {
    this.resizeColumns(100);
  }

  addEntity(entity: EntityInterface, draw: boolean = true): void {
    this.appTable.jqueryTableAddEntity(entity, draw);
  }
  addEntities(entities: Array<EntityInterface>, draw: boolean = true): void {
    this.appTable.jqueryTableAddEntities(entities, draw);
  }
  updateEntity(entity: EntityInterface, rowIndex: number, draw: boolean = true): void {
    this.appTable.jqueryTableUpdateEntity(entity, rowIndex, draw);
  }

  getAllElements(): Array<EntityInterface> {
    return this.appTable.getAllElements();
  }
  removeRow(rowIndex: number, draw: boolean = true): void {
    this.appTable.dataTableRemoveRow(rowIndex, draw);
  }
  removeRows(rowIndex: number, rows: number, draw: boolean = true): void {
    this.appTable.dataTableRemoveRows(rowIndex, rows, draw);
  }

  getSelectedEntities(): Array<EntityInterface> {
    return this.appTable.getSelectedEntities();
  }
  getSelectedEntity(): EntityInterface {
    return this.appTable.getSelectedEntity();
  }

  resizeColumns(timeOut?: number): void {
    this.appTable.resizeColumns(timeOut);
  }

  // --------------------------------------------------------------------------------
  // para buscar el registro
  findElementOnTable(element: EntityInterface, entityService: EntityServiceInterface): void {
    this.appTable.findEntityOnTable(element, entityService);
  }

  private jqueryTableInit(): void {
    // reiniciamos la tabla
    this.appTable.jqueryDataTableInitial(this.searchTableInput);
  }
  private angularTableInit(): void {
    // informamos al html para que se renderice
    this.appTableTrigger.next(undefined);
    // reiniciamos la tabla
    setTimeout(() => {
      this.appTable.angularDataTableInitial(this.searchTableInput);
    }, 200);
  }

  private angularTableChange(): void {

    console.log('Angular table change');

    this.appTableTrigger.next(undefined);

    if (!this.appTable.isInitialize) {
      this.tableInit();
    // } else {
    //   this.tableReInit();
    }
    this.appTable.resizeColumns(1000);
  }

  private jqueryTableChange(entities: Array<EntityInterface>): void {
    // reiniciamos la tabla
    this.appTable.jqueryDataTableChange(entities);
  }

  public isSelected(): boolean {
    if (this.appTable) {
      return this.appTable.getSelectedEntityCount() > 0;
    }
  }

  // ****************************** Abstractos *************************

  // aplicamos el filtro
  abstract doFilter(): void;
  // aplicamos el borrado de filtro
  abstract doRemoveFilter(): void;
  // capturamos las opciones de la tabla de json
  abstract getAppTableOptions(): AppTableOptions;
  // capturamos el fiormulario para filtros
  abstract getFilterForm(): FormGroup;
  // metodo de inicio
  abstract onInit(): void;
  // metodo de inicio
  abstract afterViewInit(): void;

}
